/**
 * Small pure hooks for World Agendas and the slot rules they answer. Existing engine code (core, climate,
 * crises, validate, resolve, ai) calls these to ask "is an agenda effect in force here?". This module
 * imports only data and types, so it never creates an import cycle.
 *
 * Timing rule (all agendas): one-time payments happen at passage (meeting turn M). Every cost, cap, ban and
 * discount applies from turn M+1 (`effectiveTurn`), so orders validated before the vote are never changed
 * by it. Hooks therefore check `s.turn >= effectiveTurn`.
 */
import type { AgendaEffects, AgendaState, AgendaVersion, CountryId, CountryState, HeatStage, Order, ProgramId, ResourceBundle, TradeProposal, VotePledge, WorldState } from '../shared/types.ts';
import { AGENDA_DOCKET, AGENDA_SCORING, FOOD, OPEN, PACT, SEAS, STAY, UNSAFE_SEAS, WINDOW, ARCTIC } from './data/agendas.ts';
import { SLOTS, type ClimateRoll, type StageRow } from './data/slots.ts';
import { MARKET } from './data/rules.ts';

export function emptyFx(): AgendaEffects {
  return {
    pact: { version: null, effectiveTurn: 99, cuts: [], account: 0 },
    seas: { version: null, effectiveTurn: 99, raidProsperityLost: {}, lent: [], duesDone: [] },
    open: { version: null, effectiveTurn: 99, banUntil: 0, fund: 0, prosperityAtPassage: {}, fundSettled: false },
    food: { version: null, effectiveTurn: 99, fundFood: 0, fundTreasury: 0, backstopUsed: [], leverageBanUntil: 0 },
    arctic: { version: null, effectiveTurn: 99, transited: [], securityLost: 0 },
    window: { version: null, effectiveTurn: 99, balance: 0, credits: [], noExpand: [] },
    dignity: { version: null, effectiveTurn: 99, shares: {} },
    stay: { version: null, effectiveTurn: 99, fund: 0, payers: [], karsovaBanUntil: 0 },
  };
}

const EMPTY: AgendaEffects = emptyFx();

/** The live agenda effects (a shared empty default for worlds without agendas; never mutate that). */
export function fx(s: WorldState): AgendaEffects {
  return s.agendaFx ?? EMPTY;
}

export function agendasOn(s: WorldState): boolean {
  return s.config.agendas !== false;
}

export function slotRulesOn(s: WorldState): boolean {
  return s.config.slotRules !== false;
}

/* ---------------------------------------------------------------------------------------------- */
/* Agenda state                                                                                    */
/* ---------------------------------------------------------------------------------------------- */

/** The meeting an agenda is reserved for in this world (its docket seat, or the next meeting after it). */
export function reservedSeat(s: WorldState, id: CountryId): number | null {
  const want = AGENDA_DOCKET[id];
  const turns = s.config.assemblyTurns.filter((x) => x <= s.config.maxTurns).slice().sort((a, b) => a - b);
  return turns.find((x) => x >= want) ?? turns[turns.length - 1] ?? null;
}

export function newAgendaState(s: WorldState, id: CountryId): AgendaState {
  return {
    id, status: 'draft', seatTurn: reservedSeat(s, id), seatKind: 'first', passedTurn: null, version: null, readingsUsed: 0,
    cosponsors: [], cosponsorRequests: [], paidOffers: {}, results: [],
  };
}

/**
 * Fills in World Agenda state for worlds created before agendas (and drops agendas of nations no longer in
 * the world). Mutates `s`; call it only on a working copy.
 */
export function ensureAgendas(s: WorldState): void {
  if (!agendasOn(s)) return;
  if (!s.agendaFx) s.agendaFx = emptyFx();
  if (!s.votePledges) s.votePledges = [];
  if (!s.agendas) s.agendas = {};
  for (const id of Object.keys(s.agendas) as CountryId[]) if (!s.nations.includes(id)) delete s.agendas[id];
  for (const id of s.nations) if (!s.agendas[id]) s.agendas[id] = newAgendaState(s, id);
}

export function agendaOf(s: WorldState, id: CountryId): AgendaState | undefined {
  return s.agendas?.[id];
}

/** Every agenda in world order. */
export function allAgendas(s: WorldState): AgendaState[] {
  const out: AgendaState[] = [];
  for (const id of s.nations) {
    const a = s.agendas?.[id];
    if (a) out.push(a);
  }
  return out;
}

/** True when a vote on this agenda is coming (a first reading, second reading or upgrade is seated). */
export function voteComing(a: AgendaState): boolean {
  return a.seatTurn !== null && (a.status === 'draft' || a.status === 'seated' || a.status === 'failed' || a.status === 'passed_amended');
}

/** Pledges and co-sponsor requests close at the lock of turn M-1. */
export function pledgesOpen(s: WorldState, a: AgendaState): boolean {
  return voteComing(a) && a.seatTurn !== null && s.turn <= a.seatTurn - 1;
}

export function activePledge(s: WorldState, from: CountryId, agenda: CountryId): VotePledge | undefined {
  return (s.votePledges ?? []).find((p) => p.from === from && p.agenda === agenda && p.status === 'active');
}

/** Whether a vote keeps a pledge (Full also keeps an Amended pledge). */
export function keepsPledge(choice: 'full' | 'amended' | 'no', vote: string | undefined): boolean {
  if (choice === 'full') return vote === 'full';
  if (choice === 'amended') return vote === 'full' || vote === 'amended';
  return vote === 'no';
}

/** 'core_four' when any seat is human (or proxy-human in the simulator), else 'ai'. */
export function agendaMode(s: WorldState): 'ai' | 'core_four' {
  const proxy = s.config.proxyHumanSeats ?? [];
  return s.nations.some((id) => (s.countries[id] && !s.countries[id]!.isAI) || proxy.includes(id)) ? 'core_four' : 'ai';
}

/** Co-sponsorships this nation holds or has requested (at most MAX_COSPONSORSHIPS per game). */
export function cosponsorCount(s: WorldState, c: CountryId): number {
  return allAgendas(s).filter((a) => a.cosponsors.includes(c) || a.cosponsorRequests.includes(c)).length;
}

type FxKey = keyof AgendaEffects;

/** The version of an agenda whose effects are in force this turn (from M+1), or null. */
export function inForce(s: WorldState, key: FxKey): AgendaVersion | null {
  const f = fx(s)[key];
  return f.version !== null && s.turn >= f.effectiveTurn ? f.version : null;
}

/** The version passed (from the passing meeting itself), or null. */
export function passed(s: WorldState, key: FxKey): AgendaVersion | null {
  return fx(s)[key].version;
}

/* ---------------------------------------------------------------------------------------------- */
/* Delta Pact                                                                                      */
/* ---------------------------------------------------------------------------------------------- */

export function pactCut(s: WorldState, c: CountryId) {
  return fx(s).pact.cuts.find((x) => x.country === c);
}

/** Green Transition discount for a nation with a pending Full Pact cut (until its deadline). */
export function pactDiscount(s: WorldState, c: CountryState): number {
  if (inForce(s, 'pact') !== 'full') return 0;
  const cut = pactCut(s, c.id);
  return cut && cut.state === 'pending' && s.turn <= cut.due ? PACT.switchDiscount : 0;
}

/** Treasury levy on Expand Fossil and Clear Forest orders, paid into the Pact Account. */
export function fossilLevy(s: WorldState): number {
  const v = inForce(s, 'pact');
  return v === 'full' ? PACT.levy : v === 'amended' ? PACT.levyAmended : 0;
}

/** Clean-tech tipping line extra depth (scaled by world size) while the Full Pact is in force. */
export function tippingExtra(s: WorldState): number {
  return s.flags.pactFullActive ? Math.round((PACT.tippingExtra * s.nations.length) / 8) : 0;
}

/* ---------------------------------------------------------------------------------------------- */
/* Seas                                                                                            */
/* ---------------------------------------------------------------------------------------------- */

export function pirateExposed(c: CountryState): boolean {
  return UNSAFE_SEAS.slots.includes(c.slot);
}

/** The Unsafe Seas rule is running this turn. */
export function seasUnsafe(s: WorldState): boolean {
  if (!agendasOn(s) || !slotRulesOn(s) || s.flags.seasSafe || inForce(s, 'seas') === 'full') return false;
  const cr = s.crises.find((x) => x.id === 'pirates');
  if (!cr || cr.status !== 'resolved') return false;
  return s.turn > cr.turn && s.turn >= UNSAFE_SEAS.fromTurn;
}

/** Nations that funded the Turn 3 naval patrol. */
export function patrolFunders(s: WorldState): number {
  return s.crises.find((x) => x.id === 'pirates')?.responders.length ?? 0;
}

/** Raid chance on a d10 this turn (0 = no raids). */
export function raidChance(s: WorldState): number {
  if (!seasUnsafe(s)) return 0;
  const patrolled = patrolFunders(s) >= UNSAFE_SEAS.patrolFunders;
  if (inForce(s, 'seas') === 'amended') return patrolled ? 0 : SEAS.amendedRaidOn;
  return patrolled ? UNSAFE_SEAS.raidOnPatrolled : UNSAFE_SEAS.raidOn;
}

/** How many trade treaties a Trade Hub counts this turn. */
export function tradeHubCap(s: WorldState, c: CountryState): number {
  const base = SLOTS[c.slot].tradeHub;
  if (base <= 0) return 0;
  const v = inForce(s, 'seas');
  if (v === 'full') return SEAS.tradeHubFull;
  if (v === 'amended') return base;
  return seasUnsafe(s) ? Math.min(base, UNSAFE_SEAS.tradeHubUnsafe) : base;
}

/** Navy units lent to the Joint Sea Patrol this turn (they cannot attack). */
export function navyLent(s: WorldState, c: CountryId): number {
  return fx(s).seas.lent.filter((x) => x.country === c && x.until >= s.turn).reduce((a, x) => a + x.units, 0);
}

/** Extra Public Support cost for attacking another nation's home land (the invitation rule). */
export function invasionPenalty(s: WorldState): number {
  return inForce(s, 'seas') === 'full' ? SEAS.invasionSupport : 0;
}

/* ---------------------------------------------------------------------------------------------- */
/* Trade, tariffs and the World Market                                                             */
/* ---------------------------------------------------------------------------------------------- */

export interface MarketCaps {
  sellEnergyMax: number;
  sellFoodMax: number;
  buyEnergyMax: number;
  buyFoodMax: number;
}

/** World Market caps (read through this function, never the constants directly). */
export function marketCaps(s: WorldState): MarketCaps {
  const open = inForce(s, 'open') === 'full';
  const food = inForce(s, 'food') === 'full';
  return {
    sellEnergyMax: open ? OPEN.sellCap : MARKET.sellEnergyMax,
    sellFoodMax: open ? OPEN.sellCap : MARKET.sellFoodMax,
    buyEnergyMax: MARKET.buyEnergyMax,
    buyFoodMax: food ? FOOD.buyFoodCap : MARKET.buyFoodMax,
  };
}

/** Why a new tariff or embargo is not allowed now, or null. */
export function tariffBlocked(s: WorldState, c: CountryState, kind: 'tariff' | 'embargo'): string | null {
  const t = s.turn;
  const open = fx(s).open;
  if (open.version !== null && t >= open.effectiveTurn && t <= open.banUntil) {
    return `The Open Markets Charter bans new tariffs and embargoes through Turn ${open.banUntil}.`;
  }
  const food = fx(s).food;
  if (kind === 'tariff' && c.id === 'ironvale' && food.version === 'full' && t >= food.effectiveTurn && t <= food.leverageBanUntil) {
    return `Under the Feed the World Pact, your nation set aside tariffs through Turn ${food.leverageBanUntil}.`;
  }
  const stay = fx(s).stay;
  if (c.id === 'karsova' && t >= stay.effectiveTurn && t <= stay.karsovaBanUntil) {
    return `Under the Right to Stay Compact, your nation may not start new tariffs through Turn ${stay.karsovaBanUntil}.`;
  }
  return null;
}

/** Public Support cost of starting a tariff or embargo after the Open Markets ban ends. */
export function lateTariffPenalty(s: WorldState): number {
  const open = fx(s).open;
  return open.version === 'full' && s.turn > open.banUntil ? OPEN.lateTariffSupport : 0;
}

/** Public Support cost for lifting one long-standing tariff (normally -3 through the Heritage League). */
export function standingLiftCost(s: WorldState): number | null {
  return inForce(s, 'open') === 'amended' ? OPEN.amendedLiftSupport : null;
}

/** A Food deal: Food moves, and no Energy does. */
export function isFoodDeal(give: ResourceBundle, get: ResourceBundle): boolean {
  return give.energy === 0 && get.energy === 0 && give.food + get.food > 0;
}

/** Food deals are exempt from tariffs and embargoes under Feed the World (either version). */
export function foodDealExempt(s: WorldState, p: Pick<TradeProposal, 'give' | 'get'>): boolean {
  return inForce(s, 'food') !== null && isFoodDeal(p.give, p.get);
}

/** Free transit to the sea for landlocked slots under the Open Markets Charter (either version). */
export function freeTransit(s: WorldState, c: CountryState): number {
  return inForce(s, 'open') !== null && OPEN.transitSlots.includes(c.slot) ? OPEN.transitTreasury : 0;
}

/* ---------------------------------------------------------------------------------------------- */
/* Costs                                                                                           */
/* ---------------------------------------------------------------------------------------------- */

/** Program cost after agenda rules (Farm Support costs 2 under Feed the World, 1 with local discounts). */
export function programCostHook(s: WorldState, c: CountryState, program: ProgramId, cost: number): number {
  if (program === 'farm_support' && inForce(s, 'food') !== null) {
    const local = c.id === 'ironvale' || c.id === 'alvera';
    return Math.min(cost, FOOD.farmSupportCost - (local ? 1 : 0));
  }
  return cost;
}

/** Heat-Safe Work costs 1 under the Right to Stay Compact (either version). */
export function heatSafeCost(s: WorldState, base: number): number {
  return inForce(s, 'stay') !== null ? Math.min(base, STAY.heatSafeCost) : base;
}

/* ---------------------------------------------------------------------------------------------- */
/* Climate                                                                                         */
/* ---------------------------------------------------------------------------------------------- */

/** Feed the World seeds: steady Food drains are 1 smaller from Stage 3. */
export function foodDrainCut(s: WorldState): number {
  return inForce(s, 'food') === 'full' && s.heat.stage >= FOOD.seedsFromStage ? FOOD.seedsDrainCut : 0;
}

/** Drought Food hits 1 smaller (Right to Stay: Full for the dry lands, Amended for everyone). */
export function droughtCut(s: WorldState, c: CountryState): number {
  const v = inForce(s, 'stay');
  if (v === 'amended') return STAY.droughtCut;
  if (v === 'full' && SLOTS[c.slot].crisis.droughtFood >= STAY.droughtLine) return STAY.droughtCut;
  return 0;
}

/** Right to Stay irrigation: +1 Food a turn from Stage 3 for the dry lands (Full). */
export function irrigationFood(s: WorldState, c: CountryState): number {
  return inForce(s, 'stay') === 'full' && s.heat.stage >= STAY.irrigationFromStage && SLOTS[c.slot].crisis.droughtFood >= STAY.droughtLine
    ? STAY.irrigationFood
    : 0;
}

/** The d10 line for a methane roll (the Permafrost Watch makes releases less likely). */
export function methaneUpTo(s: WorldState, roll: ClimateRoll): number {
  const v = inForce(s, 'arctic');
  if (v === 'full') return Math.min(roll.upTo, ARCTIC.methaneOnFull);
  if (v === 'amended') return Math.min(roll.upTo, ARCTIC.methaneOnAmended);
  return roll.upTo;
}

/** Permafrost repair upkeep this turn. */
export function permafrostUpkeep(s: WorldState, row: StageRow): number {
  return inForce(s, 'arctic') === 'full' ? Math.max(0, row.upkeep - ARCTIC.permafrostUpkeepCut) : row.upkeep;
}

/** The Arctic nation's own lane income (shared under the Full Treaty, so 0). */
export function laneIncome(s: WorldState, c: CountryState, row: StageRow): number {
  if (row.lane <= 0 || c.forces.navy < 1) return 0;
  return inForce(s, 'arctic') === 'full' && SLOTS[c.slot].arctic ? 0 : row.lane;
}

/** Forest Payments Carbon line (2 under the Just Transition Compact, else 1). */
export function forestCarbonLine(s: WorldState): number {
  return inForce(s, 'window') !== null ? WINDOW.forestCarbonLine : 1;
}

/** The stranded-asset Carbon line (5 for nations that have cut under the Full Compact). */
export function strandedLine(s: WorldState, c: CountryState, base: number): number {
  return inForce(s, 'window') === 'full' && c.carbon < c.start.carbon ? WINDOW.strandedCarbonRelief : base;
}

/** True when this nation keeps the old oil price this turn (the Just Transition bridge). */
export function oilBridge(s: WorldState, c: CountryState): boolean {
  const v = inForce(s, 'window');
  if (v === 'full') {
    if (s.turn > WINDOW.bridgeUntil) return false;
    if (!(c.diversification >= 1 || c.carbon < c.start.carbon)) return false;
    if (c.id === 'aurem' && s.turn > WINDOW.auremBridgeUntil && c.carbon > WINDOW.auremBridgeCarbon) return false;
    return true;
  }
  if (v === 'amended') return s.turn <= WINDOW.bridgeUntilAmended && c.diversification >= 1;
  return false;
}

/** Whether the tipping point's low Energy price applies this turn (the Pact safeguard can delay it). */
export function tippingLowPrice(s: WorldState): boolean {
  const ct = s.heat.cleanTechTurn;
  if (ct === null) return false;
  if (s.flags.pactFullActive) return s.turn >= Math.max(ct, PACT.lowPriceNotBefore);
  return true;
}

/** The island guarantee: a new home is promised if the sea takes an island (Dignity, either version). */
export function relocationGuarantee(s: WorldState, c: CountryState): boolean {
  return passed(s, 'dignity') !== null && SLOTS[c.slot].island;
}

/** No new fossil projects or forest clearing (Just Transition drawers; the Arctic moratorium). */
export function expandBlocked(s: WorldState, c: CountryState, action: 'expand_fossil' | 'forest_clearing'): string | null {
  if (fx(s).window.noExpand.includes(c.id)) return 'Your nation drew from the Transition Window and promised never to expand fossil fuels or clear forest.';
  if (action === 'expand_fossil' && inForce(s, 'arctic') === 'full' && SLOTS[c.slot].arctic) return 'The Arctic Treaty bans new fossil projects in the Arctic nation.';
  return null;
}

/** Programs and climate actions that Transition Window credit can pay for. */
export const CREDIT_PROGRAMS: ProgramId[] = ['diversification', 'workforce_renewal', 'family_skills', 'workforce_training'];

export function creditUsable(o: Order): boolean {
  if (o.k === 'program') return CREDIT_PROGRAMS.includes(o.program);
  if (o.k === 'climate') return o.action === 'green_transition' || o.action === 'double_green';
  return false;
}

/** Window credit this nation holds now. */
export function windowCredit(s: WorldState, c: CountryId): number {
  return fx(s).window.credits.filter((x) => x.country === c && x.expires >= s.turn).reduce((a, x) => a + x.amount, 0);
}

export function stageNow(s: WorldState): HeatStage {
  return s.heat.stage;
}

/** Author and co-sponsor points for the final results (added into CountryScore.total). */
export function agendaPoints(s: WorldState, c: CountryId): number {
  let pts = 0;
  for (const a of allAgendas(s)) {
    if (a.version === null) continue;
    const full = a.version === 'full';
    if (a.id === c) pts += full ? AGENDA_SCORING.authorFull : AGENDA_SCORING.authorAmended;
    else if (a.cosponsors.includes(c)) pts += full ? AGENDA_SCORING.cosponsorFull : AGENDA_SCORING.cosponsorAmended;
  }
  return pts;
}

/**
 * Influence for the Most Influential award. With World Agendas on: World Agenda votes on the winning side (at
 * most 1 per meeting), our own agenda passed (Full 2, Amended 1), each passed agenda we co-sponsored (1), and
 * treaties brokered. Every nation can earn these, whatever its beliefs (the standing Assembly items lean one
 * way, so they are not counted). Brokered treaties count up to INFLUENCE_BROKER_CAP. Without agendas: every Assembly vote on the winning side, plus treaties brokered.
 */
/** Brokered treaties that count toward Most Influential when World Agendas are on. */
export const INFLUENCE_BROKER_CAP = 1;

export function influence(s: WorldState, c: CountryState): number {
  if (!agendasOn(s) || !s.agendas) return c.stats.assemblyWins + c.stats.brokerCount;
  let pts = (c.stats.agendaWinsCounted ?? []).length + Math.min(INFLUENCE_BROKER_CAP, c.stats.brokerCount);
  for (const a of allAgendas(s)) {
    if (a.version === null) continue;
    if (a.id === c.id) pts += a.version === 'full' ? 2 : 1;
    else if (a.cosponsors.includes(c.id)) pts += 1;
  }
  return pts;
}

/** Adds agenda money (dues, fees, missed-cut payments) to the Loss and Damage Fund. It is not world finance. */
export function addAgendaMoney(s: WorldState, amount: number): void {
  if (amount <= 0 || !s.fund.exists) return;
  s.fund.balance += amount;
  s.fund.agendaMoney = (s.fund.agendaMoney ?? 0) + amount;
}
